/**
 * Created: 2026-09-22
 * Refactor: Split CoursesPage into a directory
 *
 * Dialog for creating or editing a course. Only super_admins of the site
 * may submit it. Pass `course` to edit an existing course, or omit it to
 * create a new one.
 */

import { useMutation } from '@apollo/client/react'
import { Field, FileField, type ISchema, message, useForm } from '@homemaster/jslib'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material'
import { SPORT_LABEL } from '@tc-app/shared/labels'
import { useState } from 'react'
import ActionQrDialog from '../../components/ActionQrDialog'
import NumberInput from '../../components/NumberInput'
import {
  type CoursesQuery,
  CreateCourseDocument,
  SportCategory,
  UpdateCourseDocument,
} from '../../gql/graphql'
import MeituanDealSelect from './MeituanDealSelect'

type Course = NonNullable<CoursesQuery['courses']>[number]

interface CourseDialogProps {
  siteId: string
  course?: Course
  onClose: () => void
  onSaved: () => void
}

interface CourseFormValues {
  name: string
  description: string
  sport: SportCategory | ''
  hours: number | null
  validDays: number | null
  price: number | null
  seats: number | null
  perUserLimit: number | null
  coverImages: string[]
  /** Meituan 套餐ID, or '' for none. */
  meituanDealId: string
}

const schema: ISchema = {
  rules: {
    name: 'required|max:64',
    hours: 'required',
    validDays: 'required',
    price: 'required',
    seats: 'required',
  },
  messages: {
    'required.name': '请填写课程名称',
    'max.name': '名称不能超过64字',
    'required.hours': '请填写课时数',
    'required.validDays': '请填写有效期',
    'required.price': '请填写价格',
    'required.seats': '请填写人数',
  },
  validate: ({ sport }: CourseFormValues, errors) => {
    if (!sport) {
      errors.add('sport', '请选择运动类型')
    }
  },
}

export default function CourseDialog({
  siteId,
  course,
  onClose,
  onSaved,
}: CourseDialogProps) {
  const isEdit = !!course
  const [createCourse] = useMutation(CreateCourseDocument)
  const [updateCourse] = useMutation(UpdateCourseDocument)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const form = useForm<CourseFormValues>(
    {
      name: course?.name ?? '',
      description: course?.description ?? '',
      sport: course?.sport ?? '',
      hours: course?.hours ?? null,
      validDays: course?.validDays ?? 365,
      price: course ? Number(course.price) : null,
      seats: course?.seats ?? 1,
      perUserLimit: course?.perUserLimit ?? null,
      coverImages: course?.coverImages ?? [],
      meituanDealId: course?.meituanDealId ?? '',
    },
    schema,
  )

  /**
   * The in-flight mobile upload, if any. `addFiles` is the way back into the
   * field that started it, handed over by the widget.
   */
  const [mobileUpload, setMobileUpload] = useState<{
    addFiles: (names: string[]) => void
    remainingSlots: number
  }>()

  const handleSubmit = async () => {
    if (form.validate(false)) return

    setSubmitting(true)
    setSubmitError('')
    try {
      const {
        name,
        description,
        sport,
        hours,
        validDays,
        price,
        seats,
        perUserLimit,
        coverImages,
        meituanDealId,
      } = form.values
      if (isEdit) {
        await updateCourse({
          variables: {
            courseInput: {
              id: course.id,
              name: name.trim(),
              description: description.trim() || null,
              hours: hours ?? 1,
              validDays: validDays ?? 365,
              price: price ?? 0,
              seats: seats ?? 1,
              perUserLimit: perUserLimit ?? null,
              coverImages: coverImages.length > 0 ? coverImages : null,
              meituanDealId: meituanDealId || null,
            },
          },
        })
        message.success('课程已保存', { autoHide: 2000 })
      } else {
        await createCourse({
          variables: {
            courseInput: {
              siteId,
              name: name.trim(),
              description: description.trim() || null,
              sport: sport as SportCategory,
              hours: hours ?? 1,
              validDays: validDays ?? 365,
              price: price ?? 0,
              seats: seats ?? 1,
              perUserLimit: perUserLimit ?? null,
              coverImages: coverImages.length > 0 ? coverImages : null,
              meituanDealId: meituanDealId || null,
            },
          },
        })
        message.success('课程已创建', { autoHide: 2000 })
      }
      onSaved()
    } catch (err) {
      setSubmitError(`操作失败: ${err instanceof Error ? err.message : err}`)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open onClose={onClose} disableRestoreFocus maxWidth="xs" fullWidth>
      <DialogTitle>{isEdit ? '编辑课程' : '新建课程'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Field
            form={form}
            name="name"
            label="课程名称"
            required
            fullWidth
            slotProps={{ htmlInput: { maxLength: 64 }, inputLabel: { shrink: true } }}
          />

          {isEdit ? (
            <Field
              label="运动类型"
              fullWidth
              readOnly
              value={SPORT_LABEL[form.values.sport as SportCategory]}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          ) : (
            <Field
              form={form}
              name="sport"
              label="运动类型"
              select
              required
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            >
              <MenuItem value={SportCategory.Tennis}>网球</MenuItem>
              <MenuItem value={SportCategory.Pickleball}>匹克球</MenuItem>
            </Field>
          )}

          <Field
            form={form}
            name="hours"
            label="课时数（1-20）"
            required
            fullWidth
            slotProps={{
              input: {
                inputComponent: NumberInput,
                inputProps: { min: 1, max: 20, decimalScale: 0 },
              },
              inputLabel: { shrink: true },
            }}
          />

          <Field
            form={form}
            name="validDays"
            label="有效期（天，自购买日起，1-366）"
            required
            fullWidth
            slotProps={{
              input: {
                inputComponent: NumberInput,
                inputProps: { min: 1, max: 366, decimalScale: 0 },
              },
              inputLabel: { shrink: true },
            }}
          />

          <Field
            form={form}
            name="price"
            label="售价（1-9999）"
            required
            fullWidth
            slotProps={{
              input: {
                inputComponent: NumberInput,
                inputProps: { min: 1, max: 9999, decimalScale: 0 },
              },
              inputLabel: { shrink: true },
            }}
          />

          <Field
            form={form}
            name="seats"
            label="人数（1-4）"
            required
            fullWidth
            slotProps={{
              input: {
                inputComponent: NumberInput,
                inputProps: { min: 1, max: 4, decimalScale: 0 },
              },
              inputLabel: { shrink: true },
            }}
          />

          <Field
            form={form}
            name="perUserLimit"
            label="每人限购（空白为不限，1-99）"
            required={false}
            fullWidth
            slotProps={{
              input: {
                inputComponent: NumberInput,
                inputProps: { min: 1, max: 99, decimalScale: 0 },
              },
              inputLabel: { shrink: true },
            }}
          />

          <Field
            form={form}
            name="description"
            label="课程说明"
            required={false}
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <MeituanDealSelect
            siteId={siteId}
            courseId={course?.id}
            value={form.values.meituanDealId}
            onChange={(meituanDealId) => form.update({ meituanDealId })}
          />

          <Box>
            <Field
              component={FileField}
              form={form}
              name="coverImages"
              label="课程封面图"
              required={false}
              onMobileUpload={setMobileUpload}
            />
          </Box>

          {submitError && (
            <Typography variant="body2" color="error">
              {submitError}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>取消</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {isEdit ? '保存' : '创建'}
        </Button>
      </DialogActions>

      {mobileUpload && (
        <ActionQrDialog
          open
          action="upload"
          title="手机上传图片"
          extraInfo={String(Math.min(mobileUpload.remainingSlots, 9))}
          onClose={() => setMobileUpload(undefined)}
          onConfirmed={(files) => {
            mobileUpload.addFiles(files)
            setMobileUpload(undefined)
            if (files.length > 0) {
              message.success(`已从手机上传 ${files.length} 张图片。`)
            }
          }}
        />
      )}
    </Dialog>
  )
}
